import { finalName, type Product } from './types'

/**
 * A bundle is a product sold as several others together.
 *
 * It is modelled as a flag and a list of member ids on an ordinary product rather than as
 * a second kind of row, because that is what it is in Salesforce too: the bundle is itself
 * a Product2 that appears on the price list and on an order, and its components are a
 * relationship hanging off it. Keeping one row type means a bundle is answered, exported,
 * discussed and migrated by everything that already handles a product, with no parallel
 * path to keep in step.
 *
 * Both fields are the client's answer, not evidence — the Salesforce export has no notion
 * of a bundle, so nothing here contradicts the grey columns.
 */
export const isBundle = (p: Product) => p.isBundle === true

/** The products a bundle holds, in the order they were chosen. */
export function bundleMembers(product: Product, all: Product[]): Product[] {
  if (!isBundle(product)) return []
  const byId = new Map(all.map((p) => [p.id, p]))
  // A member that has been removed from the catalogue is dropped rather than shown as a
  // bare id: the id means nothing to the person reading, and the answer it belonged to is
  // gone either way.
  return (product.bundleItems ?? []).flatMap((id) => {
    const found = byId.get(id)
    return found ? [found] : []
  })
}

/** What the bundle contains, as one line of prose for a table cell or an export. */
export function bundleSummary(product: Product, all: Product[]): string {
  const members = bundleMembers(product, all)
  if (members.length === 0) return 'Nothing chosen yet'
  return members.map(finalName).join(', ')
}

/**
 * What may be put into a bundle.
 *
 * A bundle never contains itself, and never contains another bundle: nesting makes the
 * price list impossible to read and gives a cycle somewhere to hide, and nothing in this
 * catalogue needs it. A product already answered Retire is left out too — a bundle that
 * sells something the client has decided to stop selling is a contradiction, and the one
 * that should be caught while the bundle is being built rather than at migration.
 */
export function eligibleBundleMembers(all: Product[], self: Product): Product[] {
  return all
    .filter((p) => p.id !== self.id && !isBundle(p) && p.keep !== 'Retire')
    .sort((a, b) => finalName(a).localeCompare(finalName(b)))
}

/**
 * The bundles that hold a product.
 *
 * This is the question that actually bites: somebody answers Retire on a product without
 * knowing a bundle sells it, and the contradiction surfaces at migration instead of on
 * the screen where it could still be fixed.
 */
export function bundlesContaining(product: Product, all: Product[]): Product[] {
  return all.filter((p) =>
    p.id !== product.id && isBundle(p) && (p.bundleItems ?? []).includes(product.id))
}

/**
 * What a bundle's contents come to at the prices actually paid for them, or null when
 * nothing in it has ever been sold. Null rather than zero: a bundle of never-sold products
 * has no total, and showing one as $0 would read as a real answer.
 */
export function partsTotal(product: Product, all: Product[]): number | null {
  const priced = bundleMembers(product, all)
    .map((m) => m.typicalPrice)
    .filter((v): v is number => v !== null)
  if (priced.length === 0) return null
  return priced.reduce((sum, v) => sum + v, 0)
}
